import { useCallback, useEffect, useState } from 'react'
import { useBlocker, type BlockerFunction } from 'react-router-dom'

/**
 * Stops a half-filled form from disappearing without being asked about first.
 *
 * There are three ways to lose one, and a guard that covers fewer than all three is worse
 * than none — it teaches people that closing is safe right up until the time it is not:
 *
 *   1. closing the form itself (a Cancel button, Escape, the backdrop) — `attempt`
 *   2. navigating elsewhere in the app — `useBlocker`
 *   3. reloading or closing the tab — `beforeunload`
 *
 * The browser will not let a page choose the wording on (3); "Leave site?" is all anyone
 * gets. `UnsavedChangesDialog` renders the prompt for (1) and (2).
 *
 * `useBlocker` needs a data router — see `main.tsx`.
 */

export interface DirtyGuard {
  /** True while the confirmation is being shown. */
  isBlocked: boolean
  /** Run `action` — immediately if the form is clean, after a confirmation if it is not. */
  attempt: (action: () => void) => void
  discard: () => void
  keepEditing: () => void
}

export function useDirtyGuard(isDirty: boolean): DirtyGuard {
  const [pending, setPending] = useState<{ run: () => void } | null>(null)

  const blocker = useBlocker(
    useCallback<BlockerFunction>(
      ({ currentLocation, nextLocation }) =>
        isDirty && currentLocation.pathname !== nextLocation.pathname,
      [isDirty],
    ),
  )

  useEffect(() => {
    if (!isDirty) return
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      // Assigning returnValue is what actually triggers the prompt in Chrome and Safari.
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [isDirty])

  const attempt = useCallback(
    (action: () => void) => {
      if (!isDirty) {
        action()
        return
      }
      setPending({ run: action })
    },
    [isDirty],
  )

  const discard = useCallback(() => {
    if (pending) {
      const { run } = pending
      setPending(null)
      run()
      return
    }
    blocker.proceed?.()
  }, [pending, blocker])

  const keepEditing = useCallback(() => {
    setPending(null)
    blocker.reset?.()
  }, [blocker])

  return {
    isBlocked: pending !== null || blocker.state === 'blocked',
    attempt,
    discard,
    keepEditing,
  }
}
